import { useState } from 'react'
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  SelectChangeEvent,
  Chip,
  Tooltip,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { type ResourceData, type ResourceFile, type ResourceState } from '../../../api/resourceService'
import { type Collection, getSchemeFields } from '../../../api/collectionService'
import { InlineSuggestion } from '../../suggestions/InlineSuggestion'


// The lifecycle states a user can pick here. Draft is the upload wizard's
// working copy, not a choice: saving publishes it (ResourceDetailModal coerces
// draft → live), so a draft shows no selector. Openness is the vault's state.
const STATE_OPTIONS: Array<{ value: ResourceState; label: string; note: string }> = [
  { value: 'live', label: 'Live', note: 'Listed, searchable and shared through any vault that includes it.' },
  { value: 'archived', label: 'Archived', note: 'Withdrawn from every listing and vault, but kept. You can set it back to Live at any time.' },
]

export interface EditableBasicInfoProps {
  resource: ResourceData
  onChange: (resource: ResourceData) => void
  errors?: Record<string, string>
  collection?: Collection | null
  /** 'identity' = Name/Description/State; 'classification' = Metadata/ResourceInfo; default = all */
  section?: 'identity' | 'classification'
  /** Multiple name variants from different source files (preferred over suggestedName). */
  suggestedNames?: Array<{ value: string; sourceFile: { id: string; filename: string } }>
  /** Multiple description variants from different source files (preferred over suggestedDescription). */
  suggestedDescriptions?: Array<{ value: string; sourceFile: { id: string; filename: string } }>
  /** Single name suggestion — used when only one source file exists (backward compat). */
  suggestedName?: string | null
  /** Single description suggestion — used when only one source file exists (backward compat). */
  suggestedDescription?: string | null
  onAcceptName?: (name: string) => void
  onAcceptDescription?: (description: string) => void
}

const RequiredMark = () => (
  <Chip
    label="required"
    size="small"
    sx={{
      ml: 1,
      height: 16,
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: 0.3,
      bgcolor: 'warning.light',
      color: 'warning.main',
      border: '1px solid',
      borderColor: 'warning.main',
      '& .MuiChip-label': { px: 0.75 },
    }}
  />
)

/**
 * Inline marker shown after a field label indicating where the current value came from.
 * Same icon (✓), colour-coded by origin so users learn the legend quickly:
 *   green   = accepted AITY suggestion (verbatim, from a specific file)
 *   orange  = AITY-generated (synthesised across components)
 *   blue    = user-set
 *   none    = never assigned
 */
const ProvenanceMark = ({ origin, sourceFilename }: { origin: ResourceData['name_origin']; sourceFilename: string | null }) => {
  if (!origin) return null
  let color: string
  let tip: string
  switch (origin) {
    case 'aity_suggestion':
      color = 'success.main'
      tip = sourceFilename ? `Accepted AITY suggestion from ${sourceFilename}` : 'Accepted AITY suggestion'
      break
    case 'aity_generated':
      color = 'warning.main'
      tip = 'Generated by AITY across components'
      break
    case 'user':
    default:
      color = 'info.main'
      tip = 'Set by user'
      break
  }
  return (
    <Tooltip title={tip} arrow>
      <CheckCircleIcon sx={{ fontSize: '1rem', ml: 0.75, color }} />
    </Tooltip>
  )
}

const VARIANTS_VISIBLE = 3

function EditableBasicInfo(props: EditableBasicInfoProps) {
  const { resource, onChange, errors = {}, collection, section,
    suggestedNames, suggestedDescriptions,
    suggestedName, suggestedDescription,
    onAcceptName, onAcceptDescription } = props

  const [showAllNames, setShowAllNames] = useState(false)
  const [showAllDescs, setShowAllDescs] = useState(false)

  // Resolve provenance source-file UUIDs to filenames so the mark's tooltip
  // can show something readable ("Accepted from pru-11.pdf").
  const filenameById = (id: string | null | undefined): string | null => {
    if (!id) return null
    const f = (resource.files ?? []).find((rf: ResourceFile) => rf.id === id)
    return f?.filename ?? null
  }
  const nameSourceFilename = filenameById(resource.name_source_file_id)
  const descSourceFilename = filenameById(resource.description_source_file_id)

  // Normalise to arrays — prefer explicit arrays, fall back to single-value props
  const nameVariants = suggestedNames ?? (suggestedName ? [{ value: suggestedName, sourceFile: { id: '_', filename: '' } }] : [])
  const descVariants = suggestedDescriptions ?? (suggestedDescription ? [{ value: suggestedDescription, sourceFile: { id: '_', filename: '' } }] : [])
  // Always surface the source filename so the user knows which file each
  // suggestion came from, even when only one file produced suggestions.
  const showNameSourceLabel = true
  const showDescSourceLabel = true
  const visibleNames = showAllNames ? nameVariants : nameVariants.slice(0, VARIANTS_VISIBLE)
  const visibleDescs = showAllDescs ? descVariants : descVariants.slice(0, VARIANTS_VISIBLE)

  // Scheme fields array from the collection's linked scheme
  const schemaFields = getSchemeFields(collection) ?? []

  // Only metadata-stored fields appear in the "Collection Metadata" form section
  const schemaFieldEntries = schemaFields.filter(f => f.storage === 'metadata')

  // Required markers on dedicated DB-level fields come from schema entries with storage: 'column'
  const isNameRequired = schemaFields.find(f => f.name === 'name')?.required ?? true
  const isDescriptionRequired = schemaFields.find(f => f.name === 'description')?.required ?? false

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...resource, name: e.target.value })

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...resource, description: e.target.value })

  const shownState: ResourceState = resource.state === 'archived' ? 'archived' : 'live'
  const handleStateChange = (e: SelectChangeEvent<ResourceState>) =>
    onChange({ ...resource, state: e.target.value as ResourceState })

  const handleMetadataFieldChange = (fieldName: string, value: string) =>
    onChange({
      ...resource,
      metadata: { ...resource.metadata, [fieldName]: value },
    })

  const showIdentity = !section || section === 'identity'
  const showClassification = !section || section === 'classification'

  return (
    <Stack spacing={3}>
      {/* ── Identity section: Name, Description, State ── */}
      {showIdentity && (
        <>
          <Box>
            <TextField
              fullWidth
              label={
                <>
                  Resource Name
                  {isNameRequired && <RequiredMark />}
                  <ProvenanceMark origin={resource.name_origin} sourceFilename={nameSourceFilename} />
                </>
              }
              value={resource.name || ''}
              onChange={handleNameChange}
              error={!!errors.name}
              helperText={errors.name || 'Max 255 characters'}
            />
            {nameVariants.length > 0 && onAcceptName && (
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {visibleNames.map(({ value, sourceFile }) => (
                  <InlineSuggestion
                    key={sourceFile.id}
                    value={value}
                    sourceLabel={showNameSourceLabel ? sourceFile.filename : undefined}
                    onApply={() => onAcceptName(value)}
                    isApplied={(resource.name ?? '') === value}
                  />
                ))}
                {nameVariants.length > VARIANTS_VISIBLE && !showAllNames && (
                  <Button size="small" onClick={() => setShowAllNames(true)} sx={{ alignSelf: 'flex-start', fontSize: '0.7rem', px: 0 }}>
                    See {nameVariants.length - VARIANTS_VISIBLE} more
                  </Button>
                )}
              </Stack>
            )}
          </Box>

          <Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={
                <>
                  Description
                  {isDescriptionRequired && <RequiredMark />}
                  <ProvenanceMark origin={resource.description_origin} sourceFilename={descSourceFilename} />
                </>
              }
              value={resource.description || ''}
              onChange={handleDescriptionChange}
              error={!!errors.description}
              helperText={errors.description || 'Max 5000 characters'}
            />
            {descVariants.length > 0 && onAcceptDescription && (
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {visibleDescs.map(({ value, sourceFile }) => (
                  <InlineSuggestion
                    key={sourceFile.id}
                    value={value}
                    multiline
                    sourceLabel={showDescSourceLabel ? sourceFile.filename : undefined}
                    onApply={() => onAcceptDescription(value)}
                    isApplied={(resource.description ?? '') === value}
                  />
                ))}
                {descVariants.length > VARIANTS_VISIBLE && !showAllDescs && (
                  <Button size="small" onClick={() => setShowAllDescs(true)} sx={{ alignSelf: 'flex-start', fontSize: '0.7rem', px: 0 }}>
                    See {descVariants.length - VARIANTS_VISIBLE} more
                  </Button>
                )}
              </Stack>
            )}
          </Box>

          {resource.id && resource.state !== 'draft' && (
            <>
              <Divider />

              <FormControl fullWidth error={!!errors.state}>
                <InputLabel id="resource-state-label">State</InputLabel>
                <Select<ResourceState>
                  labelId="resource-state-label"
                  value={shownState}
                  onChange={handleStateChange}
                  label="State"
                >
                  {STATE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color={errors.state ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 2 }}>
                  {errors.state || STATE_OPTIONS.find((o) => o.value === shownState)?.note}
                </Typography>
              </FormControl>
            </>
          )}
        </>
      )}

      {/* ── Classification section: Metadata, Resource Info ── */}
      {showClassification && (
        <>
          {schemaFieldEntries.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Collection Metadata
                  {collection?.name && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({collection.name})
                    </Typography>
                  )}
                </Typography>
                <Stack spacing={2}>
                  {schemaFieldEntries.map((f) => {
                    const label = f.display_name || f.name
                    const isRequired = f.required ?? false
                    const value = resource.metadata?.[f.name] ?? ''
                    return (
                      <TextField
                        key={f.name}
                        fullWidth
                        label={isRequired ? <>{label} <RequiredMark /></> : label}
                        value={value}
                        onChange={(e) => handleMetadataFieldChange(f.name, e.target.value)}
                        error={!!errors[`metadata.${f.name}`]}
                        helperText={errors[`metadata.${f.name}`]}
                        type={f.type === 'integer' ? 'number' : 'text'}
                        inputProps={{ maxLength: f.validators?.max_length }}
                      />
                    )
                  })}
                </Stack>
              </Box>
            </>
          )}

          {resource.id && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Resource Information
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={2}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', minWidth: 120 }}>
                      Resource ID:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {resource.id}
                    </Typography>
                  </Stack>
                  {resource.collection_id && (
                    <Stack direction="row" spacing={2}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', minWidth: 120 }}>
                        Collection ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {resource.collection_id}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </>
          )}
        </>
      )}
    </Stack>
  )
}

export default EditableBasicInfo
